import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { CounterCardService } from "services/counter-card";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { getPrintTemplate } from "./components/printTemplate";

const createRow = () => ({
  date: "",
  sellerName: "",
  openingQty: "",
  fromWarehouseQty: "",
  otherQty: "",
  soldQty: "",
  otherOutQty: "",
});

const splitDateParts = (dateText = "") => {
  const [day = "", month = "", year = ""] = String(dateText).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const hasAnyDatePart = (parts) => Boolean(parts.day || parts.month || parts.year);
const hasAllDatePart = (parts) => Boolean(parts.day && parts.month && parts.year);

const composeDate = (parts) => {
  if (!hasAnyDatePart(parts)) return "";
  const day = parts.day.padStart(2, "0");
  const month = parts.month.padStart(2, "0");
  const year = parts.year;
  return `${day}/${month}/${year}`;
};

const validateDateParts = (parts, label) => {
  if (!hasAnyDatePart(parts)) return "";
  if (!hasAllDatePart(parts)) throw new Error(`${label} chưa nhập đủ ngày/tháng/năm`);
  const day = Number(parts.day);
  const month = Number(parts.month);
  const year = Number(parts.year);
  if (month < 1 || month > 12) throw new Error(`${label} có tháng không hợp lệ`);
  if (year < 1900 || year > 9999) throw new Error(`${label} có năm không hợp lệ`);
  const maxDays = new Date(year, month, 0).getDate();
  if (day < 1 || day > maxDays) throw new Error(`${label} có ngày không hợp lệ`);
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(year)}`;
};

const parseQty = (value) => {
  const normalized = String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");
  if (!normalized) return 0;
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric) || numeric < 0) return 0;
  return Number(numeric.toFixed(3));
};

const formatQtyInput = (value) => String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");

const formatQtyOutput = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "";
  if (Math.abs(number - Math.trunc(number)) < 0.000001) return `${Math.trunc(number)}`;
  return number.toFixed(3).replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
};

const roundMoney = (value) => {
  const parsed = parseVndIntegerInput(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : 0;
};

const fmtMoney = (value) => new Intl.NumberFormat("vi-VN").format(Math.round(Number(value) || 0));

const validateRowDate = (value, label) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) throw new Error(`${label} không đúng định dạng dd/mm/yyyy`);
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const maxDay = new Date(year, month, 0).getDate();
  if (month < 1 || month > 12 || year < 1900 || year > 9999 || day < 1 || day > maxDay) {
    throw new Error(`${label} không hợp lệ`);
  }
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(year)}`;
};

export default function CounterCardDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [documentDateParts, setDocumentDateParts] = useState({
    day: String(today.getDate()).padStart(2, "0"),
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
  });

  const [header, setHeader] = useState({
    unit: "",
    address: "",
    sheetNumber: "",
    itemName: "",
    specification: "",
    unitName: "",
    unitPrice: "",
  });

  const [rows, setRows] = useState([createRow()]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await CounterCardService.getById(id);
        const data = response.data ? response.data : response;

        setHeader({
          unit: data.unit || "",
          address: data.address || "",
          sheetNumber: data.sheetNumber || "",
          itemName: data.itemName || "",
          specification: data.specification || "",
          unitName: data.unitName || "",
          unitPrice: formatVndIntegerInput(data.unitPrice || ""),
        });

        setDocumentDateParts(splitDateParts(data.documentDate || ""));

        const mappedRows = (data.rows || []).map((row) => ({
          date: row.date || "",
          sellerName: row.sellerName || "",
          openingQty: formatQtyOutput(row.openingQty),
          fromWarehouseQty: formatQtyOutput(row.fromWarehouseQty),
          otherQty: formatQtyOutput(row.otherQty),
          soldQty: formatQtyOutput(row.soldQty),
          otherOutQty: formatQtyOutput(row.otherOutQty),
        }));

        setRows(mappedRows.length ? mappedRows : [createRow()]);
      } catch (error) {
        toast.error("Không thể tải dữ liệu thẻ quầy hàng");
      }
    })();
  }, [id]);

  const derivedRows = useMemo(() =>
    rows.map((row) => {
      const openingQty = parseQty(row.openingQty);
      const fromWarehouseQty = parseQty(row.fromWarehouseQty);
      const otherQty = parseQty(row.otherQty);
      const totalQty = openingQty + fromWarehouseQty + otherQty;
      const soldQty = parseQty(row.soldQty);
      const otherOutQty = parseQty(row.otherOutQty);
      const unitPrice = roundMoney(header.unitPrice);
      const soldAmount = Math.round(soldQty * unitPrice);
      const otherOutAmount = Math.round(otherOutQty * unitPrice);
      const endingQty = totalQty - soldQty - otherOutQty;

      return {
        ...row,
        openingQty,
        fromWarehouseQty,
        otherQty,
        totalQty,
        soldQty,
        otherOutQty,
        unitPrice,
        soldAmount,
        otherOutAmount,
        endingQty,
      };
    }),
  [rows, header.unitPrice]);

  const totals = useMemo(() => {
    return derivedRows.reduce(
      (acc, row) => ({
        openingQty: acc.openingQty + row.openingQty,
        fromWarehouseQty: acc.fromWarehouseQty + row.fromWarehouseQty,
        otherQty: acc.otherQty + row.otherQty,
        totalQty: acc.totalQty + row.totalQty,
        soldQty: acc.soldQty + row.soldQty,
        soldAmount: acc.soldAmount + row.soldAmount,
        otherOutQty: acc.otherOutQty + row.otherOutQty,
        otherOutAmount: acc.otherOutAmount + row.otherOutAmount,
        endingQty: acc.endingQty + row.endingQty,
      }),
      {
        openingQty: 0,
        fromWarehouseQty: 0,
        otherQty: 0,
        totalQty: 0,
        soldQty: 0,
        soldAmount: 0,
        otherOutQty: 0,
        otherOutAmount: 0,
        endingQty: 0,
      }
    );
  }, [derivedRows]);

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setDocumentDateParts((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (field) => {
    if (field === "year") return;
    setDocumentDateParts((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const setRowField = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      let nextValue = value;
      if (["openingQty", "fromWarehouseQty", "otherQty", "soldQty", "otherOutQty"].includes(field)) {
        nextValue = formatQtyInput(value);
      }
      next[index] = { ...next[index], [field]: nextValue };
      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, createRow()]);
  };

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const address = String(header.address || "").trim();
    const documentDate = validateDateParts(documentDateParts, "Ngày lập thẻ");
    const sheetNumber = String(header.sheetNumber || "").trim();
    const itemName = String(header.itemName || "").trim();
    const specification = String(header.specification || "").trim();
    const unitName = String(header.unitName || "").trim();
    const unitPrice = roundMoney(header.unitPrice);

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!address) throw new Error("Địa chỉ không được để trống");
    if (!documentDate) throw new Error("Ngày lập thẻ không được để trống");
    if (!sheetNumber) throw new Error("Tờ số không được để trống");
    if (!itemName) throw new Error("Tên hàng không được để trống");
    if (!unitName) throw new Error("Đơn vị tính không được để trống");
    if (unitPrice <= 0) throw new Error("Đơn giá phải lớn hơn 0");

    const normalizedRows = derivedRows
      .map((row, index) => ({
        serialNumber: String(index + 1),
        date: validateRowDate(row.date, `Dòng ${index + 1}: Ngày tháng`),
        sellerName: String(row.sellerName || "").trim(),
        openingQty: row.openingQty,
        fromWarehouseQty: row.fromWarehouseQty,
        otherQty: row.otherQty,
        totalQty: row.totalQty,
        soldQty: row.soldQty,
        soldAmount: row.soldAmount,
        otherOutQty: row.otherOutQty,
        otherOutAmount: row.otherOutAmount,
        endingQty: row.endingQty,
      }))
      .filter((row) => {
        const hasQty =
          row.openingQty > 0 ||
          row.fromWarehouseQty > 0 ||
          row.otherQty > 0 ||
          row.soldQty > 0 ||
          row.otherOutQty > 0 ||
          row.soldAmount > 0 ||
          row.otherOutAmount > 0;
        return row.date || row.sellerName || hasQty;
      });

    if (!normalizedRows.length) {
      throw new Error("Thẻ quầy hàng phải có ít nhất 1 dòng dữ liệu");
    }

    normalizedRows.forEach((row, index) => {
      if (!row.date) {
        throw new Error(`Dòng ${index + 1}: Ngày tháng không được để trống`);
      }
      if (row.endingQty < 0) {
        throw new Error(`Dòng ${index + 1}: Số lượng tồn cuối ngày không hợp lệ`);
      }
    });

    return {
      unit,
      address,
      documentDate,
      sheetNumber,
      itemName,
      specification,
      unitName,
      unitPrice,
      rows: normalizedRows,
    };
  };

  const submit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await CounterCardService.create(payload);
        toast.success("Tạo thẻ quầy hàng thành công");
      } else {
        await CounterCardService.update(id, payload);
        toast.success("Cập nhật thẻ quầy hàng thành công");
      }
      navigation("/templates/counter-card");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      headerInfo: {
        ...header,
        documentDate: composeDate(documentDateParts),
        unitPrice: roundMoney(header.unitPrice),
      },
      rows: derivedRows.map((row, index) => ({ ...row, serialNumber: String(index + 1) })),
      totals,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "16px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      minWidth: 20,
    },
    inputDate: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      width: 36,
      textAlign: "center",
    },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 1200 },
    th: { border: "1px solid #000", padding: 4, textAlign: "center", fontWeight: "bold", verticalAlign: "middle" },
    td: { border: "1px solid #000", padding: 0, height: 34 },
    inputLeft: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputRight: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={s.container}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ lineHeight: 1.5 }}>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.unit}
                        onChange={(event) => setHeaderField("unit", event.target.value)}
                      />
                    </div>
                    <div>
                      <b>Địa chỉ:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.address}
                        onChange={(event) => setHeaderField("address", event.target.value)}
                      />
                    </div>
                  </div>

                  <div style={{ textAlign: "center", fontSize: 15, lineHeight: 1.35 }}>
                    <div>
                      <b>Mẫu số: 02 - BH</b>
                    </div>
                    <div>
                      <i>(Kèm theo Thông tư số 99/2025/TT-BTC</i>
                    </div>
                    <div>
                      <i>ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</i>
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "center", marginTop: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 26, textTransform: "uppercase" }}>
                    THẺ QUẦY HÀNG
                  </div>
                  <div style={{ marginTop: 8 }}>
                    Ngày lập thẻ{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={documentDateParts.day}
                      onChange={(event) => setDateField("day", event.target.value)}
                      onBlur={() => padDateField("day")}
                    />
                    {" / "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={documentDateParts.month}
                      onChange={(event) => setDateField("month", event.target.value)}
                      onBlur={() => padDateField("month")}
                    />
                    {" / "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDate, width: 56 }}
                      value={documentDateParts.year}
                      onChange={(event) => setDateField("year", event.target.value)}
                    />
                    {" "}Tờ số:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                      value={header.sheetNumber}
                      onChange={(event) => setHeaderField("sheetNumber", event.target.value)}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 10, lineHeight: 1.7 }}>
                  <div>
                    - Tên hàng:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 320 }}
                      value={header.itemName}
                      onChange={(event) => setHeaderField("itemName", event.target.value)}
                    />
                    {" "}Quy cách:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 200 }}
                      value={header.specification}
                      onChange={(event) => setHeaderField("specification", event.target.value)}
                    />
                  </div>
                  <div>
                    - Đơn vị tính:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 160 }}
                      value={header.unitName}
                      onChange={(event) => setHeaderField("unitName", event.target.value)}
                    />
                    {" "}Đơn giá:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 160, textAlign: "right" }}
                      value={header.unitPrice}
                      onChange={(event) => setHeaderField("unitPrice", formatVndIntegerInput(event.target.value))}
                    />
                  </div>
                </div>

                <div style={{ overflowX: "auto", border: "1px solid #ccc", marginTop: 10 }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th rowSpan={2} style={{ ...s.th, width: 90 }}>Ngày tháng</th>
                        <th rowSpan={2} style={{ ...s.th, width: 140 }}>Tên người bán hàng</th>
                        <th rowSpan={2} style={{ ...s.th, width: 90 }}>Số lượng tồn đầu ngày (ca)</th>
                        <th rowSpan={2} style={{ ...s.th, width: 90 }}>Số lượng nhập từ kho trong ngày (ca)</th>
                        <th rowSpan={2} style={{ ...s.th, width: 90 }}>Số lượng nhập khác trong ngày (ca)</th>
                        <th rowSpan={2} style={{ ...s.th, width: 90 }}>Cộng tồn đầu ngày (ca) và nhập trong ngày (ca)</th>
                        <th colSpan={2} style={s.th}>Xuất bán</th>
                        <th colSpan={2} style={s.th}>Xuất khác</th>
                        <th rowSpan={2} style={{ ...s.th, width: 90 }}>Số lượng tồn cuối ngày (ca)</th>
                        {!containView ? <th rowSpan={2} style={{ ...s.th, width: 40 }}>#</th> : null}
                      </tr>
                      <tr>
                        <th style={s.th}>Lượng</th>
                        <th style={s.th}>Tiền</th>
                        <th style={s.th}>Lượng</th>
                        <th style={s.th}>Tiền</th>
                      </tr>
                      <tr>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>2</th>
                        <th style={s.th}>3</th>
                        <th style={s.th}>4 = 1+2+3</th>
                        <th style={s.th}>5</th>
                        <th style={s.th}>6</th>
                        <th style={s.th}>7</th>
                        <th style={s.th}>8</th>
                        <th style={s.th}>9 = 4-5-7</th>
                        {!containView ? <th style={s.th} /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={`counter-row-${index}`}>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputCenter}
                              value={row.date}
                              onChange={(event) => setRowField(index, "date", event.target.value)}
                              placeholder="dd/mm/yyyy"
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputLeft}
                              value={row.sellerName}
                              onChange={(event) => setRowField(index, "sellerName", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.openingQty}
                              onChange={(event) => setRowField(index, "openingQty", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.fromWarehouseQty}
                              onChange={(event) => setRowField(index, "fromWarehouseQty", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.otherQty}
                              onChange={(event) => setRowField(index, "otherQty", event.target.value)}
                            />
                          </td>
                          <td style={{ ...s.td, textAlign: "right", padding: "0 6px" }}>
                            {formatQtyOutput(derivedRows[index]?.totalQty || 0)}
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.soldQty}
                              onChange={(event) => setRowField(index, "soldQty", event.target.value)}
                            />
                          </td>
                          <td style={{ ...s.td, textAlign: "right", padding: "0 6px" }}>
                            {fmtMoney(derivedRows[index]?.soldAmount || 0)}
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.otherOutQty}
                              onChange={(event) => setRowField(index, "otherOutQty", event.target.value)}
                            />
                          </td>
                          <td style={{ ...s.td, textAlign: "right", padding: "0 6px" }}>
                            {fmtMoney(derivedRows[index]?.otherOutAmount || 0)}
                          </td>
                          <td style={{ ...s.td, textAlign: "right", padding: "0 6px" }}>
                            {formatQtyOutput(derivedRows[index]?.endingQty || 0)}
                          </td>
                          {!containView ? (
                            <td style={{ ...s.td, textAlign: "center" }}>
                              <button
                                type="button"
                                onClick={() => removeRow(index)}
                                style={{ border: "none", background: "transparent", color: "#d32f2f", fontWeight: 700, cursor: "pointer" }}
                              >
                                x
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      ))}

                      <tr>
                        <td colSpan={2} style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>Cộng</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{formatQtyOutput(totals.openingQty)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{formatQtyOutput(totals.fromWarehouseQty)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{formatQtyOutput(totals.otherQty)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{formatQtyOutput(totals.totalQty)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{formatQtyOutput(totals.soldQty)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{fmtMoney(totals.soldAmount)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{formatQtyOutput(totals.otherOutQty)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{fmtMoney(totals.otherOutAmount)}</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{formatQtyOutput(totals.endingQty)}</td>
                        {!containView ? <td style={s.td} /> : null}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
                  <SoftButton variant="outlined" color="dark" onClick={() => navigation(-1)}>
                    Trở về
                  </SoftButton>

                  <div style={{ display: "flex", gap: 12 }}>
                    {!containView ? (
                      <SoftButton variant="outlined" color="dark" onClick={addRow}>
                        THÊM DÒNG
                      </SoftButton>
                    ) : null}

                    {containView ? (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In Bảng
                      </SoftButton>
                    ) : null}

                    {!containView ? (
                      <SoftButton variant="gradient" color="dark" onClick={submit} disabled={loadingSubmit}>
                        {id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    ) : null}
                  </div>
                </div>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
